/**
 * Onglet Gestion des utilisateurs
 * @module admin/tabs/UsersTab
 */

import React, { useEffect, useRef } from 'react';
import { useUsers } from '../hooks/useUsers';
import StatCard from '../components/StatCard';
import AdminResult from '../components/AdminResult';
import AdminModal from '../components/AdminModal';

const UsersTab = () => {
  const {
    users,
    usersLoading,
    usersError,
    usersPagination,
    usersFilters,
    usersStats,
    selectedUser,
    editUserModal,
    editUserData,
    setEditUserData,
    setEditUserModal,
    createUserModal,
    createUserData,
    setCreateUserData,
    setCreateUserModal,
    fetchUsers,
    fetchUsersStats,
    handleUserFilterChange,
    handleUserPageChange,
    handleCreateUser,
    handleEditUser,
    handleSaveUser,
    handleDeleteUser,
    selectedUserIds,
    toggleUserRowSelected,
    toggleSelectAllUsersOnPage,
    handleBulkDeleteUsers,
    handleOpenChangePassword,
    handleChangePassword,
    handleActivateUser,
    changePasswordModal,
    changePasswordUser,
    changePasswordValue,
    setChangePasswordValue,
    setChangePasswordModal,
    setChangePasswordUser,
  } = useUsers();

  useEffect(() => {
    fetchUsers();
    fetchUsersStats();
  }, [fetchUsers, fetchUsersStats]);

  const selectAllPageRef = useRef(null);
  useEffect(() => {
    const el = selectAllPageRef.current;
    if (!el || users.length === 0) {
      return;
    }
    const pageIds = users.map((u) => u.id);
    const n = pageIds.filter((id) =>
      selectedUserIds.some((s) => String(s) === String(id))
    ).length;
    el.indeterminate = n > 0 && n < pageIds.length;
  }, [users, selectedUserIds]);

  const allUsersOnPageSelected =
    users.length > 0 &&
    users.every((u) =>
      selectedUserIds.some((s) => String(s) === String(u.id))
    );

  const handleRefresh = () => {
    fetchUsers();
    fetchUsersStats();
  };

  return (
    <section className="admin-section">
      <div className="admin-section-header">
        <h2>Gestion des utilisateurs</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => setCreateUserModal(true)}
            disabled={usersLoading}
            className="admin-button admin-button-primary"
          >
            Ajouter un utilisateur
          </button>
          <button
            onClick={handleRefresh}
            disabled={usersLoading}
            className="admin-button admin-button-secondary"
          >
            {usersLoading ? 'Chargement...' : 'Actualiser'}
          </button>
        </div>
      </div>

      {usersError && (
        <AdminResult isSuccess={false} title="Erreur" message={usersError} />
      )}

      {usersStats && (
        <div className="admin-stats-grid" style={{ marginBottom: '2rem' }}>
          <StatCard title="Total">
            <p style={{ fontSize: '2rem', fontWeight: 'bold' }}>
              {usersStats.total}
            </p>
          </StatCard>
          <StatCard title="Actifs">
            <p
              style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4caf50' }}
            >
              {usersStats.active}
            </p>
          </StatCard>
          <StatCard title="Inactifs">
            <p
              style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f44336' }}
            >
              {usersStats.inactive}
            </p>
          </StatCard>
          <StatCard title="Avec Twitch">
            <p style={{ fontSize: '2rem', fontWeight: 'bold' }}>
              {usersStats.withTwitch}
            </p>
          </StatCard>
          <StatCard title="Nouveaux (30j)">
            <p style={{ fontSize: '2rem', fontWeight: 'bold' }}>
              {usersStats.recent}
            </p>
          </StatCard>
        </div>
      )}

      <div className="admin-form" style={{ marginBottom: '1.5rem' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
          }}
        >
          <div className="admin-field">
            <label htmlFor="userSearch">Recherche</label>
            <input
              type="text"
              id="userSearch"
              placeholder="Username, email, nom..."
              value={usersFilters.search}
              onChange={(e) => handleUserFilterChange('search', e.target.value)}
            />
          </div>
          <div className="admin-field">
            <label htmlFor="userRole">Rôle</label>
            <select
              id="userRole"
              value={usersFilters.role}
              onChange={(e) => handleUserFilterChange('role', e.target.value)}
            >
              <option value="">Tous</option>
              <option value="admin">Admin</option>
              <option value="viewer">Viewer</option>
              <option value="moderator">Moderator</option>
            </select>
          </div>
          <div className="admin-field">
            <label htmlFor="userActive">Statut</label>
            <select
              id="userActive"
              value={usersFilters.isActive}
              onChange={(e) =>
                handleUserFilterChange('isActive', e.target.value)
              }
            >
              <option value="">Tous</option>
              <option value="true">Actifs</option>
              <option value="false">Inactifs</option>
            </select>
          </div>
        </div>
      </div>

      {users.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1rem',
          }}
        >
          <span style={{ color: '#555' }}>
            {selectedUserIds.length} compte(s) sélectionné(s)
          </span>
          <button
            type="button"
            className="admin-button admin-button-danger"
            disabled={usersLoading || selectedUserIds.length === 0}
            onClick={handleBulkDeleteUsers}
            data-testid="admin-users-bulk-delete"
          >
            Supprimer définitivement la sélection
          </button>
        </div>
      )}

      {usersLoading && users.length === 0 ? (
        <p className="admin-loading">Chargement des utilisateurs...</p>
      ) : users.length === 0 ? (
        <p className="admin-info">Aucun utilisateur trouvé.</p>
      ) : (
        <>
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: '2.5rem' }}>
                    <input
                      ref={selectAllPageRef}
                      type="checkbox"
                      checked={allUsersOnPageSelected}
                      title="Sélectionner la page"
                      aria-label="Sélectionner tous les utilisateurs de la page"
                      onChange={toggleSelectAllUsersOnPage}
                    />
                  </th>
                  <th>ID</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Nom</th>
                  <th>Rôle</th>
                  <th>Provider</th>
                  <th>Twitch</th>
                  <th>Premium</th>
                  <th>Statut</th>
                  <th>Créé le</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className={!user.isActive ? 'admin-table-row-inactive' : ''}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedUserIds.some(
                          (s) => String(s) === String(user.id)
                        )}
                        onChange={() => toggleUserRowSelected(user.id)}
                        aria-label={`Sélectionner ${user.username || user.id}`}
                      />
                    </td>
                    <td>{user.id}</td>
                    <td>{user.username || '-'}</td>
                    <td>{user.email || '-'}</td>
                    <td>{user.name || user.twitchDisplayName || '-'}</td>
                    <td>
                      <span className={`admin-badge admin-badge-${user.role}`}>
                        {user.role}
                      </span>
                    </td>
                    <td>{user.provider || '-'}</td>
                    <td>
                      {user.twitchLogin ? (
                        <span title={user.twitchId}>@{user.twitchLogin}</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <span
                        className={`admin-badge ${
                          user.isPremium
                            ? 'admin-badge-premium'
                            : 'admin-badge-secondary'
                        }`}
                      >
                        {user.isPremium ? 'Premium' : '-'}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`admin-badge ${
                          user.isActive
                            ? 'admin-badge-success'
                            : 'admin-badge-error'
                        }`}
                      >
                        {user.isActive ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td>
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString('fr-FR')
                        : '-'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="admin-button admin-button-small"
                          onClick={() => handleEditUser(user)}
                          title="Modifier"
                        >
                          ✏️
                        </button>
                        <button
                          className="admin-button admin-button-small"
                          onClick={() => handleOpenChangePassword(user)}
                          title="Changer le mot de passe"
                        >
                          🔑
                        </button>
                        {user.isActive ? (
                          <button
                            className="admin-button admin-button-small admin-button-danger"
                            onClick={() => handleDeleteUser(user.id, false)}
                            title="Désactiver"
                          >
                            🚫
                          </button>
                        ) : (
                          <button
                            className="admin-button admin-button-small admin-button-success"
                            onClick={() => handleActivateUser(user.id)}
                            title="Réactiver"
                          >
                            ✅
                          </button>
                        )}
                        <button
                          className="admin-button admin-button-small admin-button-danger"
                          onClick={() => handleDeleteUser(user.id, true)}
                          title="Supprimer définitivement"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {usersPagination.totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '1rem',
                marginTop: '1.5rem',
              }}
            >
              <button
                className="admin-button admin-button-secondary"
                onClick={() => handleUserPageChange(usersPagination.page - 1)}
                disabled={usersPagination.page === 1 || usersLoading}
              >
                Précédent
              </button>
              <span>
                Page {usersPagination.page} sur {usersPagination.totalPages} (
                {usersPagination.total} utilisateurs)
              </span>
              <button
                className="admin-button admin-button-secondary"
                onClick={() => handleUserPageChange(usersPagination.page + 1)}
                disabled={
                  usersPagination.page >= usersPagination.totalPages ||
                  usersLoading
                }
              >
                Suivant
              </button>
            </div>
          )}
        </>
      )}

      <AdminModal
        isOpen={createUserModal}
        onClose={() => {
          setCreateUserModal(false);
          setCreateUserData({
            username: '',
            email: '',
            name: '',
            password: '',
            role: 'viewer',
            isActive: true,
          });
        }}
        title="Ajouter un utilisateur"
        footer={
          <>
            <button
              className="admin-button admin-button-secondary"
              onClick={() => setCreateUserModal(false)}
            >
              Annuler
            </button>
            <button
              className="admin-button admin-button-primary"
              onClick={handleCreateUser}
              disabled={usersLoading}
            >
              {usersLoading ? 'Création...' : 'Créer'}
            </button>
          </>
        }
      >
        <div className="admin-form">
          <div className="admin-field">
            <label htmlFor="createUsername">Nom d&apos;utilisateur *</label>
            <input
              type="text"
              id="createUsername"
              value={createUserData.username}
              onChange={(e) =>
                setCreateUserData({
                  ...createUserData,
                  username: e.target.value,
                })
              }
              placeholder="ex: monuser"
              autoComplete="username"
            />
          </div>
          <div className="admin-field">
            <label htmlFor="createPassword">Mot de passe *</label>
            <input
              type="password"
              id="createPassword"
              value={createUserData.password}
              onChange={(e) =>
                setCreateUserData({
                  ...createUserData,
                  password: e.target.value,
                })
              }
              placeholder="Min. 6 caractères"
              autoComplete="new-password"
            />
          </div>
          <div className="admin-field">
            <label htmlFor="createEmail">Email</label>
            <input
              type="email"
              id="createEmail"
              value={createUserData.email}
              onChange={(e) =>
                setCreateUserData({ ...createUserData, email: e.target.value })
              }
              placeholder="optionnel"
            />
          </div>
          <div className="admin-field">
            <label htmlFor="createName">Nom affiché</label>
            <input
              type="text"
              id="createName"
              value={createUserData.name}
              onChange={(e) =>
                setCreateUserData({ ...createUserData, name: e.target.value })
              }
              placeholder="optionnel"
            />
          </div>
          <div className="admin-field">
            <label htmlFor="createRole">Rôle</label>
            <select
              id="createRole"
              value={createUserData.role}
              onChange={(e) =>
                setCreateUserData({ ...createUserData, role: e.target.value })
              }
            >
              <option value="viewer">Viewer</option>
              <option value="moderator">Moderator</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="admin-field">
            <label htmlFor="createIsActive">Statut</label>
            <select
              id="createIsActive"
              value={createUserData.isActive === true ? 'true' : 'false'}
              onChange={(e) =>
                setCreateUserData({
                  ...createUserData,
                  isActive: e.target.value === 'true',
                })
              }
            >
              <option value="true">Actif</option>
              <option value="false">Inactif</option>
            </select>
          </div>
        </div>
      </AdminModal>

      <AdminModal
        isOpen={editUserModal}
        onClose={() => setEditUserModal(false)}
        title={`Modifier l'utilisateur: ${selectedUser?.username || selectedUser?.id || ''}`}
        footer={
          <>
            <button
              className="admin-button admin-button-secondary"
              onClick={() => setEditUserModal(false)}
            >
              Annuler
            </button>
            <button
              className="admin-button admin-button-primary"
              onClick={handleSaveUser}
              disabled={usersLoading}
            >
              {usersLoading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </>
        }
      >
        <div className="admin-form">
          <div className="admin-field">
            <label htmlFor="editRole">Rôle</label>
            <select
              id="editRole"
              value={editUserData.role}
              onChange={(e) =>
                setEditUserData({ ...editUserData, role: e.target.value })
              }
            >
              <option value="viewer">Viewer</option>
              <option value="moderator">Moderator</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="admin-field">
            <label htmlFor="editIsActive">Statut</label>
            <select
              id="editIsActive"
              value={editUserData.isActive}
              onChange={(e) =>
                setEditUserData({
                  ...editUserData,
                  isActive: e.target.value === 'true',
                })
              }
            >
              <option value="true">Actif</option>
              <option value="false">Inactif</option>
            </select>
          </div>
          <div className="admin-field">
            <label htmlFor="editIsPremium">
              <input
                type="checkbox"
                id="editIsPremium"
                checked={editUserData.isPremium === true}
                onChange={(e) =>
                  setEditUserData({
                    ...editUserData,
                    isPremium: e.target.checked,
                  })
                }
              />{' '}
              Compte premium
            </label>
            <small
              style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}
            >
              Accorde l&apos;accès aux fonctionnalités premium
            </small>
          </div>
          {editUserData.isPremium && (
            <div className="admin-field">
              <label htmlFor="editPremiumExpiresAt">
                Expiration premium (optionnel)
              </label>
              <input
                type="datetime-local"
                id="editPremiumExpiresAt"
                value={
                  editUserData.premiumExpiresAt
                    ? new Date(editUserData.premiumExpiresAt)
                        .toISOString()
                        .slice(0, 16)
                    : ''
                }
                onChange={(e) =>
                  setEditUserData({
                    ...editUserData,
                    premiumExpiresAt: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : '',
                  })
                }
              />
              <small
                style={{
                  display: 'block',
                  marginTop: '0.25rem',
                  color: '#666',
                }}
              >
                Vide = premium permanent
              </small>
            </div>
          )}
          <div className="admin-field">
            <label htmlFor="editEmail">Email</label>
            <input
              type="email"
              id="editEmail"
              value={editUserData.email}
              onChange={(e) =>
                setEditUserData({ ...editUserData, email: e.target.value })
              }
            />
          </div>
          <div className="admin-field">
            <label htmlFor="editName">Nom</label>
            <input
              type="text"
              id="editName"
              value={editUserData.name}
              onChange={(e) =>
                setEditUserData({ ...editUserData, name: e.target.value })
              }
            />
          </div>
          <div className="admin-field">
            <label htmlFor="editTwitchLogin">Twitch Login</label>
            <input
              type="text"
              id="editTwitchLogin"
              value={editUserData.twitchLogin}
              onChange={(e) =>
                setEditUserData({
                  ...editUserData,
                  twitchLogin: e.target.value,
                })
              }
            />
          </div>
          <div className="admin-field">
            <label htmlFor="editTwitchDisplayName">Twitch Display Name</label>
            <input
              type="text"
              id="editTwitchDisplayName"
              value={editUserData.twitchDisplayName}
              onChange={(e) =>
                setEditUserData({
                  ...editUserData,
                  twitchDisplayName: e.target.value,
                })
              }
            />
          </div>
          <div className="admin-field">
            <label htmlFor="editProfileImageUrl">URL Image de profil</label>
            <input
              type="url"
              id="editProfileImageUrl"
              value={editUserData.profileImageUrl}
              onChange={(e) =>
                setEditUserData({
                  ...editUserData,
                  profileImageUrl: e.target.value,
                })
              }
            />
          </div>
        </div>
      </AdminModal>

      <AdminModal
        isOpen={changePasswordModal}
        onClose={() => {
          setChangePasswordModal(false);
          setChangePasswordUser(null);
          setChangePasswordValue('');
        }}
        title={`Changer le mot de passe: ${changePasswordUser?.username || changePasswordUser?.id || ''}`}
        footer={
          <>
            <button
              className="admin-button admin-button-secondary"
              onClick={() => setChangePasswordModal(false)}
            >
              Annuler
            </button>
            <button
              className="admin-button admin-button-primary"
              onClick={handleChangePassword}
              disabled={usersLoading || !changePasswordValue || changePasswordValue.length < 6}
            >
              {usersLoading ? 'Enregistrement...' : 'Modifier le mot de passe'}
            </button>
          </>
        }
      >
        <div className="admin-form">
          <div className="admin-field">
            <label htmlFor="changePasswordNew">Nouveau mot de passe *</label>
            <input
              type="password"
              id="changePasswordNew"
              value={changePasswordValue}
              onChange={(e) => setChangePasswordValue(e.target.value)}
              placeholder="Min. 6 caractères"
              autoComplete="new-password"
            />
            <small style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
              Le mot de passe doit contenir au moins 6 caractères. L&apos;utilisateur pourra se connecter avec ce nouveau mot de passe.
            </small>
          </div>
        </div>
      </AdminModal>
    </section>
  );
};

export default UsersTab;
